import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Stack } from "@mui/material";

import { TableFiltersPanelContent } from "./TableFiltersPanelContent";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { FilterConfigRegistry } from "@/components/FilterBox/filter-config-registry";
import { FilterBoxConfig, FilterBoxStateProvider } from "@/components/FilterBox/FilterBoxStateContext";
import { BaseFilterInput, FilterCombinator } from "@/components/FilterBox/types";
import { TableFiltersMobileDrawer } from "@/dashboard-design-system/DashboardTableCustomization/TableFilters/TableFiltersMobileDrawer";

export interface TableFiltersPanelProps<T extends BaseFilterInput> {
  isOpen: boolean;
  isMobileOpen?: boolean;
  isInMobileDrawer?: boolean;
  onMobileClose?: () => void;
  registry: FilterConfigRegistry<T>;
  filters: T[];
  getFilterIdentity?: (filters: T[]) => string;
  onChange: (filters: T[]) => void;
  combinator?: FilterCombinator;
  onCombinatorChange?: (combinator: FilterCombinator) => void;
  config?: Omit<FilterBoxConfig<T>, "registry">;
  preventSaveAsList?: (filters: T[]) => boolean;
  defaultMobileFilterKey?: string;
  openedFrom?: string;
  onReset?: () => void;
  onEmpty?: () => void;
  focusedFilterId?: string;
  onFilterFocused?: () => void;
}

const FILTER_APPLY_DELAY_MS = 600;

export const TableFiltersPanel = <T extends BaseFilterInput>({
  isOpen,
  isMobileOpen,
  isInMobileDrawer = false,
  onMobileClose,
  registry,
  filters,
  getFilterIdentity = stringifyFilters,
  onChange,
  onReset,
  onEmpty,
  focusedFilterId,
  onFilterFocused,
  combinator = "AND",
  onCombinatorChange,
  config,
  preventSaveAsList,
  defaultMobileFilterKey,
  openedFrom,
}: TableFiltersPanelProps<T>) => {
  const { isSmallScreen } = useMediaQuery();
  const filterIdentity = getFilterIdentity(filters);
  const lastSubmittedIdentity = useRef(filterIdentity);
  const pendingSubmissions = useRef(new Set<string>());
  const pendingChange = useRef<number | undefined>(undefined);
  const onChangeRef = useRef(onChange);
  const committedFilterIdentity = useRef(filterIdentity);
  const [externalFilters, setExternalFilters] = useState({ identity: filterIdentity, version: 0 });
  const [defaultMobileFilter, setDefaultMobileFilter] = useState<T>();

  const cancelPendingChange = () => {
    window.clearTimeout(pendingChange.current);
    pendingChange.current = undefined;
  };

  useEffect(() => () => window.clearTimeout(pendingChange.current), [isOpen, isMobileOpen, isSmallScreen]);

  useLayoutEffect(() => {
    onChangeRef.current = onChange;
    if (committedFilterIdentity.current !== filterIdentity) {
      committedFilterIdentity.current = filterIdentity;
      if (!pendingSubmissions.current.delete(filterIdentity)) {
        cancelPendingChange();
        pendingSubmissions.current.clear();
        lastSubmittedIdentity.current = filterIdentity;
      }
    }
  });

  if (externalFilters.identity !== filterIdentity) {
    const isOwnSubmission = pendingSubmissions.current.has(filterIdentity);
    setExternalFilters({
      identity: filterIdentity,
      version: externalFilters.version + (isOwnSubmission ? 0 : 1),
    });
  }

  const submitFilters = (next: T[]) => {
    const nextIdentity = getFilterIdentity(next);
    pendingSubmissions.current.add(nextIdentity);
    lastSubmittedIdentity.current = nextIdentity;
    onChangeRef.current(next);
  };

  if (!isOpen || (isSmallScreen && isMobileOpen === false)) {
    return null;
  }
  const amountFilterConfig = defaultMobileFilterKey ? registry.get(defaultMobileFilterKey) : undefined;
  if (isSmallScreen && filters.length === 0 && !defaultMobileFilter && amountFilterConfig) {
    setDefaultMobileFilter(amountFilterConfig.createDefault());
  }
  const content = (
    <FilterBoxStateProvider<T>
      key={`${externalFilters.version}:${defaultMobileFilter?.id ?? ""}`}
      registry={registry}
      initialFilters={isSmallScreen && filters.length === 0 && defaultMobileFilter ? [defaultMobileFilter] : filters}
      features={{ search: false, combinator: false, saveAsList: false }}
      {...config}
      preventSaveAsList={preventSaveAsList}
      initialFilterCombinator={combinator}
      onFiltersChange={(next) => {
        if (!isSmallScreen) {
          const validFilters = next.filter((filter) => filter.isMandatory || registry.validate(filter));
          const nextIdentity = getFilterIdentity(validFilters);
          cancelPendingChange();
          if (nextIdentity !== lastSubmittedIdentity.current) {
            if (next.length === 0) {
              submitFilters(validFilters);
            } else {
              pendingChange.current = window.setTimeout(() => submitFilters(validFilters), FILTER_APPLY_DELAY_MS);
            }
          }
        }
      }}
    >
      <TableFiltersPanelContent<T>
        openedFrom={openedFrom}
        focusedFilterId={focusedFilterId}
        onFilterFocused={onFilterFocused}
        onCombinatorChange={onCombinatorChange}
        onEmpty={() => {
          if (isSmallScreen) {
            return;
          }
          cancelPendingChange();
          const mandatoryFilters = filters.filter((filter) => filter.isMandatory);
          if (lastSubmittedIdentity.current !== getFilterIdentity(mandatoryFilters)) {
            submitFilters(mandatoryFilters);
          }
          onEmpty?.();
        }}
        onChange={(next, nextCombinator) => {
          const nextIdentity = getFilterIdentity(next);
          if (isSmallScreen || nextIdentity !== lastSubmittedIdentity.current) {
            cancelPendingChange();
            submitFilters(next);
          }
          onCombinatorChange?.(nextCombinator);
          if (isSmallScreen) {
            onMobileClose?.();
          }
        }}
        onReset={() => {
          cancelPendingChange();
          const resetIdentity = getFilterIdentity([]);
          pendingSubmissions.current.add(resetIdentity);
          lastSubmittedIdentity.current = resetIdentity;
          if (onReset) {
            onReset();
          } else {
            onChange([]);
          }
          if (isSmallScreen) {
            onMobileClose?.();
          }
        }}
      />
    </FilterBoxStateProvider>
  );
  if (isSmallScreen && isInMobileDrawer) {
    return content;
  }
  return isSmallScreen ? (
    <TableFiltersMobileDrawer open={isMobileOpen ?? isOpen} onClose={onMobileClose}>
      {content}
    </TableFiltersMobileDrawer>
  ) : (
    <Stack
      sx={(theme) => ({
        borderRadius: theme.radius(1),
        backgroundColor: theme.palette.surface.neutral.supershy,
      })}
    >
      {content}
    </Stack>
  );
};

const stringifyFilters = (filters: BaseFilterInput[]) => JSON.stringify(filters);
